import React from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "@aws-amplify/auth";
import {
  Users,
  DollarSign,
  Award,
  Trophy,
  Settings,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const handleSignOut = async () => {
    try {
      await signOut();
      console.log("Successfully signed out");
      navigate("/auth?state=signIn");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const StatCard = ({ title, value, subtitle, icon }) => (
    <div className={`shadow-xl ${
      isDarkMode 
        ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5" 
        : "bg-base-100 border border-base-200"
    } rounded-lg`}>
      <div className="card-body">
        <div className="flex items-center gap-3">
          {React.cloneElement(icon, {
            className: "w-5 h-5 text-primary",
          })}
          <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>{title}</span>
        </div>
        <div className="mt-3">
          <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-base-content"}`}>{value}</p>
          <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"} mt-1`}>{subtitle}</p>
        </div>
      </div>
    </div>
  );

  const CardContainer = ({ children, title, icon, className = "" }) => (
    <div className={`shadow-xl ${
      isDarkMode 
        ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5" 
        : "bg-base-100 border border-base-200"
    } rounded-lg ${className}`}>
      <div className="card-body">
        <div className="flex items-center gap-3 mb-4">
          {React.cloneElement(icon, {
            className: "w-5 h-5 text-primary",
          })}
          <h3 className={`card-title ${isDarkMode ? "text-white" : ""}`}>{title}</h3>
        </div>
        {children}
      </div>
    </div>
  );

  const ActionButton = ({ children, onClick, className = "" }) => (
    <button
      onClick={onClick}
      className={`btn btn-primary btn-outline w-full ${className}`}
    >
      {children}
    </button>
  );

  const StatBox = ({ label, value }) => (
    <div className={`shadow ${
      isDarkMode 
        ? "bg-[#252D42]/50 border border-white/10" 
        : "bg-base-200"
    } rounded-lg`}>
      <div className="stat">
        <div className={`stat-title ${isDarkMode ? "text-gray-400" : ""}`}>{label}</div>
        <div className={`stat-value text-xl ${isDarkMode ? "text-white" : ""}`}>{value}</div>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : ""}`}>Convention Dashboard</h1>
        <p className={isDarkMode ? "text-gray-400" : "text-base-content/70"}>
          Manage events, registrations, and competitions
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Registrations"
          value="487"
          subtitle="Next event: Atlanta"
          icon={<Users />}
        />
        <StatCard
          title="Competition Entries"
          value="156"
          subtitle="42 solos, 114 groups"
          icon={<Trophy />}
        />
        <StatCard
          title="Revenue"
          value="$158.2k"
          subtitle="+18% from last event"
          icon={<DollarSign />}
        />
        <StatCard
          title="Scholarships"
          value="24"
          subtitle="Elite & Stand Out"
          icon={<Award />}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Registration Overview */}
        <CardContainer
          title="Registration Overview"
          icon={<Users />}
          className="lg:col-span-2"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatBox label="Mini (8-10)" value="124" />
            <StatBox label="Junior (11-12)" value="156" />
            <StatBox label="Teen (13-15)" value="143" />
            <StatBox label="Senior (16+)" value="64" />
          </div>
        </CardContainer>

        {/* Quick Actions */}
        <CardContainer title="Quick Actions" icon={<Settings />}>
          <div className="space-y-3">
            <ActionButton>Generate Studio Codes</ActionButton>
            <ActionButton>Process Scholarships</ActionButton>
          </div>
        </CardContainer>

        {/* Competition Management */}
        <CardContainer
          title="Competition Management"
          icon={<Trophy />}
          className="lg:col-span-2"
        >
          <div className="space-y-4">
            <div className={`card ${
              isDarkMode ? "bg-[#252D42]/50 border border-white/10" : "bg-base-200"
            }`}>
              <div className="card-body">
                <div className="flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className={`font-medium text-lg ${isDarkMode ? "text-white" : ""}`}>Solo Division</h3>
                    <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
                      42 entries / 3 rooms
                    </p>
                  </div>
                  <ActionButton className="w-auto">
                    Manage Schedule
                  </ActionButton>
                </div>
              </div>
            </div>
            <div className={`card ${
              isDarkMode ? "bg-[#252D42]/50 border border-white/10" : "bg-base-200"
            }`}>
              <div className="card-body">
                <div className="flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className={`font-medium text-lg ${isDarkMode ? "text-white" : ""}`}>Group Division</h3>
                    <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
                      114 entries / 2 rooms
                    </p>
                  </div>
                  <ActionButton className="w-auto">
                    Manage Schedule
                  </ActionButton>
                </div>
              </div>
            </div>
          </div>
        </CardContainer>

        {/* Scholarship Tracking */}
        <CardContainer title="Scholarship Tracking" icon={<Award />}>
          <div className="space-y-4">
            <div className={`card ${
              isDarkMode ? "bg-[#252D42]/50 border border-white/10" : "bg-base-200"
            }`}>
              <div className="card-body">
                <h3 className={`font-medium ${isDarkMode ? "text-white" : ""}`}>Elite Scholarships</h3>
                <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
                  12 awarded this event
                </p>
                <progress
                  className="progress progress-primary w-full mt-2"
                  value="50"
                  max="100"
                ></progress>
              </div>
            </div>
            <div className={`card ${
              isDarkMode ? "bg-[#252D42]/50 border border-white/10" : "bg-base-200"
            }`}>
              <div className="card-body">
                <h3 className={`font-medium ${isDarkMode ? "text-white" : ""}`}>Stand Out Awards</h3>
                <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
                  8 awarded this event
                </p>
                <progress
                  className="progress progress-secondary w-full mt-2"
                  value="33"
                  max="100"
                ></progress>
              </div>
            </div>
          </div>
        </CardContainer>
      </div>
    </div>
  );
};

export default AdminDashboard;